import { useMemo, useState } from 'react';
import { TextField } from '@mui/material';
import { ScorerOpenView } from '../../../../types/advise';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { ConfirmDialog } from '../ConfirmDialog';
import { FunctionScoringTable } from '../SideBySideView/FunctionScoringTable';
import { ratingKey } from '../SideBySideView/SupplierScoring.calc';
import { ScorerScopeCard } from './ScorerScopeCard';

// Copies upstack_api's Engagements::ScorerSchemas::ANSWERS comment maxLength.
const COMMENT_LIMIT = 2000;

// The scoring table keys a rating by supplier and function; this form has only the one supplier.
const SUPPLIER_KEY = 'scorer';

interface ScorerFormProps {
  view: ScorerOpenView;
  // 0–100 by function id; an unrated function is absent.
  ratings: Map<string, number>;
  comment: string;
  saveFailed: boolean;
  submitting: boolean;
  submitRefusal: string | null;
  onRate: (functionId: string, rating: number) => void;
  onCommentChange: (comment: string) => void;
  onSubmit: () => Promise<void>;
}

/** The scorer's one supplier to rate, function by function, with an optional comment and the final submit. */
export const ScorerForm = ({
  view,
  ratings,
  comment,
  saveFailed,
  submitting,
  submitRefusal,
  onRate,
  onCommentChange,
  onSubmit
}: ScorerFormProps) => {
  const [confirming, setConfirming] = useState(false);
  const total = view.categories.reduce((count, category) => count + category.functions.length, 0);
  const unrated = total - ratings.size;
  const tableRatings = useMemo(
    () => new Map([...ratings].map(([functionId, rating]) => [ratingKey(SUPPLIER_KEY, functionId), rating])),
    [ratings]
  );

  return (
    <>
      <ScorerScopeCard view={view} />
      <section className="rounded-lg border border-grey-2 bg-white py-4">
        <TextBlock
          size="lg"
          weight="semibold"
          color="grey8"
          className="px-5 pb-2">
          {view.supplier.supplier_name}
        </TextBlock>
        <FunctionScoringTable
          categories={view.categories}
          supplierId={SUPPLIER_KEY}
          ratings={tableRatings}
          onRate={onRate}
          disabled={submitting}
          stackOnPhone
        />
      </section>
      <TextField
        multiline
        minRows={2}
        value={comment}
        disabled={submitting}
        onChange={(event) => onCommentChange(event.target.value)}
        placeholder="Anything else your Advisor should know? (optional)"
        inputProps={{ maxLength: COMMENT_LIMIT, 'aria-label': 'Comments for your Advisor' }}
        sx={{ backgroundColor: 'white' }}
      />
      {saveFailed && (
        <TextSpan
          size="sm"
          color="amber">
          Your latest changes haven&apos;t saved yet. They&apos;ll be sent with your next change, or when you submit.
        </TextSpan>
      )}
      {submitRefusal && (
        <TextSpan
          size="sm"
          color="red">
          {submitRefusal}
        </TextSpan>
      )}
      <div>
        <button
          type="button"
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          disabled={ratings.size === 0 || submitting}
          onClick={() => (unrated > 0 ? setConfirming(true) : onSubmit())}>
          {submitting ? <ComponentSpinner sx={{ height: '19px' }} /> : 'Submit My Scores'}
        </button>
      </div>
      <ConfirmDialog
        open={confirming}
        title="Submit with unrated functions?"
        message={
          `${unrated} of ${total} functions are still unrated. ` +
          "They'll be sent as unrated, and you can't change your scores after submitting."
        }
        confirmLabel="Submit My Scores"
        cancelLabel="Keep Scoring"
        confirmColor="primary"
        onConfirm={async () => {
          setConfirming(false);
          await onSubmit();
        }}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
};
